import { useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import Lightbox from "@/components/Lightbox";
import { motionProjects } from "@/data/portfolio";

const isVideoFile = (url) => /\.(mp4|webm|mov)(\?.*)?$/i.test(url || "");

const MotionVideo = ({ src, poster, className }) => {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.muted = true;
        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) el.play().catch(() => {});
                else el.pause();
            },
            { threshold: 0.25 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    return (
        <video
            ref={ref}
            src={src}
            poster={poster}
            muted
            loop
            playsInline
            preload="metadata"
            className={className}
        />
    );
};

const MotionGraphics = () => {
    const [active, setActive] = useState(null);

    return (
        <section data-testid="motion-section" className="relative py-12 sm:py-16">
            <div
                className="pointer-events-none absolute left-1/2 top-0 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-flame/[0.06] blur-[160px]"
                aria-hidden="true"
            />
            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <SectionHeading
                    index={4}
                    eyebrow="Portfolio"
                    title={
                        <>
                            Motion{" "}
                            <em className="font-serifit text-[1.06em] font-medium italic text-[#f6e3d3]">
                                Graphics
                            </em>
                        </>
                    }
                    subtitle="Bringing ideas to life through motion, design and visual storytelling."
                />

                <div className="columns-1 gap-6 sm:columns-2 lg:columns-3">
                    {motionProjects.map((p, i) => (
                        <Reveal key={p.id} delay={(i % 3) * 0.08} className="mb-6 break-inside-avoid">
                            <button
                                data-testid={`motion-card-${p.id}`}
                                onClick={() => setActive(p)}
                                className="group relative block w-full overflow-hidden rounded-3xl border border-white/10 bg-ink-3 text-left shadow-[0_20px_60px_-25px_rgba(0,0,0,0.9)] transition-[border-color,box-shadow] duration-500 hover:border-flame/40 hover:shadow-[0_28px_80px_-25px_rgba(255,75,38,0.22)]"
                            >
                                {isVideoFile(p.video) ? (
                                    <MotionVideo
                                        src={p.video}
                                        poster={p.thumbnail || undefined}
                                        className="aspect-video w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                                    />
                                ) : (
                                    <img
                                        src={p.thumbnail}
                                        alt={p.title}
                                        loading="lazy"
                                        className="aspect-video w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                                    />
                                )}
                                <div
                                    className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent"
                                    aria-hidden="true"
                                />
                            </button>
                        </Reveal>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {active && <Lightbox project={active} onClose={() => setActive(null)} />}
            </AnimatePresence>
        </section>
    );
};

export default MotionGraphics;
